/**
 * SeasonRosterPage — the association's season board, for admins and delegates.
 *
 * Everyone on the board in one list: active members UNION anyone who competed
 * at one of the association's rodeos that year (see docs/season-winnings.md).
 * A member who entered nothing sits at $0 and a non-member who won money is
 * still on the board, because the association pays both.
 *
 * Money is two halves — MANAGED winnings recomputed from live standings, and
 * ADDITIONAL winnings a delegate typed in for rodeos HighCall doesn't run.
 * Neither the totals nor the managed half are stored; the roster endpoint
 * recomputes the season on every miss, which is why it is the one query here.
 *
 * Every column sorts on header click. The Finals badge is the one edit made
 * from the board itself: clicking it flips eligibility optimistically and
 * writes ONLY that field, so a delegate's winnings and notes are never touched.
 *
 * Association and year live in the query string so a board can be linked to.
 */

import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useDebounce } from '@/hooks/useDebounce';
import { Loader2, Search, Trophy, AlertCircle } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { badgeVariants } from '@/components/ui/badge';
import { EmptyState } from '@/components/ui/empty-state';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { YearSelect } from '@/components/common/YearSelect';
import { useToast } from '@/contexts/ToastContext';
import {
  useSeasonAssociations,
  useSeasonRoster,
  useUpsertSeasonAdjustment,
  type SeasonRosterRow,
} from '@/api/extensions/useSeasonWinningsOps';
import { cn } from '@/lib/utils';
import { formatCurrency } from '@/utils/formatters';

/** The cross-org sentinel org is a permission construct, never a real board. */
const ALL_ORG_ID = '00000000-0000-0000-0000-000000000001';

type SortKey =
  | 'name'
  | 'member'
  | 'entered'
  | 'placed'
  | 'attendance'
  | 'managed'
  | 'additional'
  | 'total'
  | 'finals';
type SortOrder = 'asc' | 'desc';

function attendanceRatio(row: SeasonRosterRow, rodeoCount: number): number {
  return rodeoCount > 0 ? row.rodeos_attended / rodeoCount : 0;
}

/** "3 / 9 (33%)" — the raw counts matter as much as the percentage. */
function attendanceLabel(row: SeasonRosterRow, rodeoCount: number): string {
  const pct = Math.round(attendanceRatio(row, rodeoCount) * 100);
  return `${row.rodeos_attended} / ${rodeoCount} (${pct}%)`;
}

/** "Attended 3 of 9 rodeos (33%)" — the rule most associations decide finals by. */
function attendanceHint(row: SeasonRosterRow, rodeoCount: number): string {
  const pct = Math.round(attendanceRatio(row, rodeoCount) * 100);
  return `Attended ${row.rodeos_attended} of ${rodeoCount} rodeos (${pct}%)`;
}

/** What the Member # cell shows — and therefore what it sorts by. */
function memberLabel(row: SeasonRosterRow): string {
  return row.is_member ? row.member_number || '--' : 'Non-member';
}

function sortValue(row: SeasonRosterRow, key: SortKey, rodeoCount: number): number {
  switch (key) {
    case 'entered':
      return row.events_entered;
    case 'placed':
      return row.events_placed;
    case 'attendance':
      return attendanceRatio(row, rodeoCount);
    case 'managed':
      return row.managed_winnings;
    case 'additional':
      return row.additional_winnings;
    case 'total':
      return row.total_winnings;
    case 'finals':
      return row.finals_eligible ? 1 : 0;
    default:
      return 0;
  }
}

function compareRows(
  a: SeasonRosterRow,
  b: SeasonRosterRow,
  key: SortKey,
  order: SortOrder,
  rodeoCount: number,
): number {
  const direction = order === 'asc' ? 1 : -1;
  const byName = (a.display_name ?? '').localeCompare(b.display_name ?? '');
  if (key === 'name') return byName * direction;
  const delta =
    key === 'member'
      ? memberLabel(a).localeCompare(memberLabel(b), undefined, { numeric: true })
      : sortValue(a, key, rodeoCount) - sortValue(b, key, rodeoCount);
  // Ties fall back to name so the list order is stable between renders.
  return delta !== 0 ? delta * direction : byName;
}

function matchesSearch(row: SeasonRosterRow, term: string): boolean {
  if (!term) return true;
  const haystack = `${row.display_name ?? ''} ${row.member_number ?? ''}`.toLowerCase();
  return haystack.includes(term);
}

export default function SeasonRosterPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { showError } = useToast();

  const orgId = searchParams.get('org') ?? '';
  const year = Number(searchParams.get('year')) || new Date().getFullYear();

  const [search, setSearch] = useState('');
  // The list filters in memory, but re-filtering and re-sorting 400+ rows on
  // every keystroke still stutters — settle the term before applying it.
  const debouncedSearch = useDebounce(search, 300);
  const [sortBy, setSortBy] = useState<SortKey>('total');
  const [sortOrder, setSortOrder] = useState<SortOrder>('desc');

  // Org dropdown source: every association with a season to show, most active
  // first — the same list the Standings board offers. A delegate can pick one
  // they hold no role at; the roster endpoint 403s that, rendered inline below.
  const { data: associations } = useSeasonAssociations();
  const orgOptions = useMemo(
    () =>
      (associations ?? [])
        .filter((o) => o.organization_id !== ALL_ORG_ID)
        .map((o) => ({
          id: o.organization_id,
          name: o.short_name || o.organization_name || 'Association',
        })),
    [associations],
  );

  // Land on a board rather than an empty page: pick the first association
  // once the list arrives, and recover if a linked org isn't on it.
  useEffect(() => {
    if (orgId || orgOptions.length === 0) return;
    const next = new URLSearchParams(searchParams);
    next.set('org', orgOptions[0].id);
    setSearchParams(next, { replace: true });
  }, [orgId, orgOptions, searchParams, setSearchParams]);

  const setParam = (key: 'org' | 'year', value: string) => {
    const next = new URLSearchParams(searchParams);
    next.set(key, value);
    setSearchParams(next, { replace: true });
  };

  const handleSort = (key: SortKey) => {
    if (sortBy === key) setSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    else {
      setSortBy(key);
      // Text reads A→Z; every number reads biggest first, eligible first.
      setSortOrder(key === 'name' || key === 'member' ? 'asc' : 'desc');
    }
  };

  const { data, isLoading, error } = useSeasonRoster(orgId || undefined, year);
  const isForbidden = (error as (Error & { status?: number }) | null)?.status === 403;

  // Finals toggles in flight, keyed by contestant, holding the value the badge
  // already shows. An entry lives from the click until the write settles: on
  // success the roster has refetched with the new value by then; on failure
  // dropping the entry reverts the badge to what the server still holds.
  const save = useUpsertSeasonAdjustment(orgId, year);
  const [finalsPending, setFinalsPending] = useState<Record<string, boolean>>({});

  const toggleFinals = (row: SeasonRosterRow) => {
    const id = row.user_profile_id;
    const next = !row.finals_eligible;
    setFinalsPending((prev) => ({ ...prev, [id]: next }));
    save.mutate(
      { userId: id, finals_eligible: next },
      {
        onError: (err) =>
          showError('Could not save finals eligibility', (err as Error).message),
        onSettled: () =>
          setFinalsPending((prev) => {
            const rest = { ...prev };
            delete rest[id];
            return rest;
          }),
      },
    );
  };

  const rodeoCount = data?.rodeo_count ?? 0;
  const rows = useMemo(() => {
    const term = debouncedSearch.trim().toLowerCase();
    const filtered = (data?.contestants ?? [])
      .filter((r) => matchesSearch(r, term))
      .map((r) =>
        r.user_profile_id in finalsPending
          ? { ...r, finals_eligible: finalsPending[r.user_profile_id] }
          : r,
      );
    return filtered.sort((a, b) => compareRows(a, b, sortBy, sortOrder, rodeoCount));
  }, [data?.contestants, debouncedSearch, sortBy, sortOrder, rodeoCount, finalsPending]);

  // Summed over what's on screen — with a search active the footer answers
  // "what do these contestants add up to", not "what did the season pay".
  const shownTotal = useMemo(
    () => rows.reduce((sum, r) => sum + r.total_winnings, 0),
    [rows],
  );

  const openContestant = (userId: string) => {
    navigate(`/delegate/season-roster/${orgId}/${userId}?year=${year}`);
  };

  const headerProps = { sortBy, sortOrder, onSort: handleSort };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Season Roster</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Every contestant on this association's season board — members and anyone
          who competed at its rodeos. Click a row to open their breakdown and set
          additional winnings; click the Finals badge to toggle eligibility.
        </p>
      </div>

      {/* Association + season */}
      <div className="rounded-lg border bg-card text-card-foreground shadow-sm p-4">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <div>
            <label className="text-xs font-medium text-muted-foreground mb-1 block">
              Association
            </label>
            <Select value={orgId} onValueChange={(v) => setParam('org', v)}>
              <SelectTrigger className="h-9" aria-label="Association">
                <SelectValue placeholder="Select an association" />
              </SelectTrigger>
              <SelectContent>
                {orgOptions.map((o) => (
                  <SelectItem key={o.id} value={o.id}>
                    {o.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs font-medium text-muted-foreground mb-1 block">
              Season
            </label>
            <YearSelect
              value={year}
              onChange={(next) => setParam('year', String(next ?? new Date().getFullYear()))}
              className="h-9 w-full"
              aria-label="Season year"
            />
          </div>
          <div>
            <label className="text-xs font-medium text-muted-foreground mb-1 block">
              Search
            </label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                className="h-9 pl-9"
                placeholder="Name or member number"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                aria-label="Search contestants"
              />
            </div>
          </div>
        </div>
      </div>

      <p className="text-xs text-muted-foreground">
        Totals include go-round money as well as average payouts, so they can read
        higher than a contestant's dashboard Event History.
      </p>

      <div className="rounded-lg border bg-card text-card-foreground shadow-sm overflow-hidden">
        {isForbidden ? (
          <div className="p-12">
            <EmptyState
              icon={Trophy}
              title="Not your association"
              description="You don't hold a role at this association, so its season board isn't yours to read. Pick one you do."
            />
          </div>
        ) : error ? (
          <div className="p-6 flex items-start gap-3">
            <AlertCircle className="h-5 w-5 text-destructive mt-0.5" />
            <div>
              <p className="text-sm font-medium text-destructive">
                Could not load the season roster
              </p>
              <p className="text-sm text-destructive/80 mt-1">
                {(error as Error).message || 'Unknown error'}
              </p>
            </div>
          </div>
        ) : isLoading ? (
          <div className="p-12 text-center">
            <Loader2 className="h-6 w-6 animate-spin mx-auto text-muted-foreground" />
          </div>
        ) : rows.length === 0 ? (
          <div className="p-12 text-center text-sm text-muted-foreground">
            {search.trim()
              ? 'No contestants match that search.'
              : `Nobody is on the ${year} board for this association yet.`}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" aria-label="Season roster">
              <thead className="bg-muted/40 text-xs uppercase tracking-wide text-muted-foreground">
                <tr>
                  <SortableHeader label="Contestant" sortKey="name" align="left" {...headerProps} />
                  <SortableHeader label="Member #" sortKey="member" align="left" {...headerProps} />
                  <SortableHeader label="Entered" sortKey="entered" {...headerProps} />
                  <SortableHeader label="Placed" sortKey="placed" {...headerProps} />
                  <SortableHeader label="Rodeos" sortKey="attendance" {...headerProps} />
                  <SortableHeader label="Managed" sortKey="managed" {...headerProps} />
                  <SortableHeader label="Additional" sortKey="additional" {...headerProps} />
                  <SortableHeader label="Total" sortKey="total" {...headerProps} />
                  <SortableHeader label="Finals" sortKey="finals" align="left" {...headerProps} />
                </tr>
              </thead>
              <tbody className="divide-y">
                {rows.map((row) => (
                  <tr
                    key={row.user_profile_id}
                    tabIndex={0}
                    role="button"
                    onClick={() => openContestant(row.user_profile_id)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        openContestant(row.user_profile_id);
                      }
                    }}
                    className="cursor-pointer transition-colors hover:bg-muted/40 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset"
                  >
                    <td className="px-4 py-2 font-medium">
                      {row.display_name || 'Unnamed contestant'}
                    </td>
                    <td className="px-4 py-2 text-muted-foreground">{memberLabel(row)}</td>
                    <td className="px-4 py-2 text-right">{row.events_entered}</td>
                    <td className="px-4 py-2 text-right">{row.events_placed}</td>
                    <td className="px-4 py-2 text-right tabular-nums">
                      {attendanceLabel(row, rodeoCount)}
                    </td>
                    <td className="px-4 py-2 text-right tabular-nums">
                      {formatCurrency(row.managed_winnings)}
                    </td>
                    <td className="px-4 py-2 text-right tabular-nums">
                      {formatCurrency(row.additional_winnings)}
                    </td>
                    <td className="px-4 py-2 text-right tabular-nums font-semibold">
                      {formatCurrency(row.total_winnings)}
                    </td>
                    <td className="px-4 py-2">
                      <FinalsBadge
                        eligible={row.finals_eligible}
                        pending={row.user_profile_id in finalsPending}
                        hint={attendanceHint(row, rodeoCount)}
                        onToggle={() => toggleFinals(row)}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="bg-muted/40 text-sm font-medium">
                <tr>
                  <td className="px-4 py-2" colSpan={7}>
                    {rows.length} {rows.length === 1 ? 'contestant' : 'contestants'}
                  </td>
                  <td className="px-4 py-2 text-right tabular-nums">
                    {formatCurrency(shownTotal)}
                  </td>
                  <td />
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Sortable column header. Click toggles direction on the active column and
 * otherwise starts from the natural reading order the page picks.
 */
function SortableHeader({
  label,
  sortKey,
  sortBy,
  sortOrder,
  onSort,
  align = 'right',
}: {
  label: string;
  sortKey: SortKey;
  sortBy: SortKey;
  sortOrder: SortOrder;
  onSort: (key: SortKey) => void;
  align?: 'left' | 'right';
}) {
  const isActive = sortBy === sortKey;
  return (
    <th
      scope="col"
      aria-sort={isActive ? (sortOrder === 'asc' ? 'ascending' : 'descending') : 'none'}
      className={`font-medium px-4 py-2 ${align === 'right' ? 'text-right' : 'text-left'}`}
    >
      <button
        type="button"
        onClick={() => onSort(sortKey)}
        className={`inline-flex items-center gap-1 select-none transition-colors hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 ${
          isActive ? 'text-foreground' : ''
        }`}
      >
        {label}
        {isActive ? <span aria-hidden="true">{sortOrder === 'asc' ? '▲' : '▼'}</span> : null}
      </button>
    </th>
  );
}

/**
 * The Finals cell: a badge that is also the toggle. It swallows the click and
 * the Enter/Space keys so flipping eligibility never also opens the row.
 */
function FinalsBadge({
  eligible,
  pending,
  hint,
  onToggle,
}: {
  eligible: boolean;
  pending: boolean;
  hint: string;
  onToggle: () => void;
}) {
  return (
    <button
      type="button"
      aria-pressed={eligible}
      disabled={pending}
      title={`${eligible ? 'Mark ineligible for finals' : 'Mark eligible for finals'} — ${hint}`}
      onClick={(e) => {
        e.stopPropagation();
        onToggle();
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') e.stopPropagation();
      }}
      className={cn(
        badgeVariants({ variant: 'outline' }),
        'cursor-pointer disabled:cursor-wait disabled:opacity-60',
        eligible
          ? 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border-emerald-500/30 hover:bg-emerald-500/25'
          : 'bg-muted text-muted-foreground border-transparent hover:bg-muted/70',
      )}
    >
      {eligible ? 'Eligible' : 'Ineligible'}
    </button>
  );
}
